import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError, EMPTY, Subject } from 'rxjs';
import {
  tap,
  catchError,
  switchMap,
  map,
  filter,
  distinctUntilChanged,
  shareReplay,
  finalize,
} from 'rxjs/operators';
import {
  Message,
  CreateChatRequest,
  ContinueChatRequest,
  CreateFeedbackRequest,
  CreateMessageFeedbackRequest,
  BranchChatRequest,
  EditAndRerunRequest,
  CancelRun200Response,
  ListChatItem,
  ChatService,
  AdminService,
  AdminGetThread200Response,
} from 'ldt-chat-api';
import { AuthService } from '../../auth/service/auth.service';
import { NotificationService } from '../../shared/notification-service/notification.service';
import { Log } from '../../logger.service';
import { LangGraphInstanceService } from './langgraph-instance.service';
// ============================================
// Extended Types (API fields not in package)
// ============================================

/**
 * Extended response type for getChat/createChat/continueChat
 * Based on observed API responses in chat.component.ts:319-332
 */
export interface ChatThreadResponse {
  id: string;
  org_id?: string;
  status: 'idle' | 'busy' | 'error' | 'interrupted';
  messages: Message[];
  company_lookups?: Record<string, string>; // company_id -> company_name
  intercom_handoff?: {
    original_query: string;
  };
  artifact_pending?: boolean; // True when the thread is done but an artifact is still generating
  created_at?: string;
  updated_at?: string;
}

/**
 * Internal polling state tracker
 * Not exposed in API - manages service-side polling
 */
interface PollingState {
  threadId: string;
  timeoutId: number;
  startTime: number;
  maxDuration: number;
  interval: number;
  isActive: boolean;
  retryCount: number;
  currentBackoff: number;
  refCount: number; // Track number of active subscribers
}

/**
 * Service configuration options
 * Allows customization of polling, caching, and retry behavior
 */
export interface ChatServiceConfig {
  pollingInterval: number; // Default: 2000ms
  maxPollingDuration: number; // Default: 240000ms (4 min)
  initialPollDelay: number; // Default: 3000ms
  cacheThreadsMaxAge: number; // Default: 300000ms (5 min)
  retryAttempts: number; // Default: 3
  retryDelayMs: number; // Default: 1000ms
}

/**
 * Cached thread with metadata
 * Wraps ChatThreadResponse with cache timestamp
 */
interface CachedThread extends ChatThreadResponse {
  _cachedAt: number;
}

/**
 * Default service configuration
 */
const DEFAULT_CONFIG: ChatServiceConfig = {
  pollingInterval: 5000,
  maxPollingDuration: 360000, // 6 minutes: extended to accommodate artifact generation after thread completes
  initialPollDelay: 3000,
  cacheThreadsMaxAge: 300000,
  retryAttempts: 3,
  retryDelayMs: 1000,
};

/**
 * Centralized chat service for managing chat threads, polling, and state
 *
 * Consolidates chat API interface logic, caching, polling, and state management
 * previously scattered across multiple components.
 *
 * SHARED POLLING MECHANISM:
 * - Polling is shared across all consumers of the same thread
 * - Uses reference counting: polling continues as long as at least one component subscribes
 * - When all subscriptions are dropped, polling stops (unless thread is still busy)
 * - Multiple components can subscribe to threadById$() without creating duplicate polls
 *
 * @example
 * ```typescript
 * // Create new thread
 * this.chatService.createThread(orgId, 'What is workforce growth?')
 *   .subscribe(thread => console.log(thread));
 *
 * // Continue existing thread
 * this.chatService.continueThread(orgId, threadId, 'Tell me more')
 *   .subscribe(thread => console.log(thread));
 *
 * // Subscribe to thread updates (automatically manages polling)
 * this.chatService.threadById$(threadId)
 *   .subscribe(thread => this.currentThread = thread);
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class PeopleChatService {
  // Core State Management
  private threadsCache$ = new BehaviorSubject<Map<string, CachedThread>>(new Map());
  private activePolls = new Map<string, PollingState>();

  // Chat list cache (per org)
  private chatListCache$ = new BehaviorSubject<
    Map<string, { list: ListChatItem[]; cachedAt: number }>
  >(new Map());

  // Loading States
  private loadingThreads$ = new BehaviorSubject<Set<string>>(new Set());

  // Error States
  private errors$ = new BehaviorSubject<Map<string, Error>>(new Map());

  // In-flight requests (for deduplication)
  private inFlightRequests = new Map<string, Observable<ChatThreadResponse>>();

  // Thread completion events
  private threadCompleteSubject$ = new Subject<ChatThreadResponse>();
  public threadComplete$: Observable<ChatThreadResponse> =
    this.threadCompleteSubject$.asObservable();

  // Service configuration
  private config: ChatServiceConfig;

  constructor(
    private chatApi: ChatService,
    private authService: AuthService,
    private notify: NotificationService,
    private langGraphInstance: LangGraphInstanceService,
    private adminApi: AdminService
  ) {
    this.config = DEFAULT_CONFIG;
    this.initializeOrgChangeHandler();
  }

  // ============================================
  // Public Observable Streams
  // ============================================

  /**
   * Get thread by ID as observable
   * Returns null if thread not found in cache
   * Only emits when the thread actually changes (not on every cache update)
   * Automatically manages polling reference counting
   */
  public threadById$(threadId: string): Observable<ChatThreadResponse | null> {
    return new Observable<ChatThreadResponse | null>((observer) => {
      // Increment reference count when subscription starts
      this.incrementPollingRefCount(threadId);

      // Subscribe to the underlying cache stream
      const subscription = this.threadsCache$
        .pipe(
          map((cache) => cache.get(threadId) || null),
          distinctUntilChanged((prev, curr) => {
            // Only emit if the thread actually changed
            // Compare key fields to avoid unnecessary re-renders
            if (prev === curr) return true; // Same reference
            if (!prev || !curr) return prev === curr; // One is null

            // Compare key fields that would trigger UI updates
            return (
              prev.id === curr.id &&
              prev.status === curr.status &&
              prev.artifact_pending === curr.artifact_pending &&
              prev.messages.length === curr.messages.length &&
              JSON.stringify(prev.company_lookups) === JSON.stringify(curr.company_lookups) &&
              // Compare last message to detect content changes
              (prev.messages.length === 0 ||
                JSON.stringify(prev.messages[prev.messages.length - 1]) ===
                  JSON.stringify(curr.messages[curr.messages.length - 1]))
            );
          })
        )
        .subscribe(observer);

      // Cleanup: decrement reference count when unsubscribed
      return () => {
        subscription.unsubscribe();
        this.decrementPollingRefCount(threadId);
      };
    });
  }

  /**
   * Check if thread is currently loading
   */
  public isThreadLoading$(threadId: string): Observable<boolean> {
    return this.loadingThreads$.pipe(map((loading) => loading.has(threadId)));
  }

  /**
   * Get error for specific thread
   * Returns null if no error
   */
  public threadError$(threadId: string): Observable<Error | null> {
    return this.errors$.pipe(map((errors) => errors.get(threadId) || null));
  }

  /**
   * Get completion observable for specific thread
   * Filters global threadComplete$ to only emit when the specified thread completes
   * @param threadId - The thread ID to monitor for completion
   * @returns Observable that emits once when the specified thread reaches a terminal status
   */
  public threadCompleteById$(threadId: string): Observable<ChatThreadResponse> {
    return this.threadComplete$.pipe(filter((thread) => thread.id === threadId));
  }

  /**
   * Get chat list for organization as observable
   * Returns cached list if available and not stale
   * @param orgId - The organization ID
   * @returns Observable that emits the chat list when available
   */
  public chatList$(orgId: string): Observable<ListChatItem[]> {
    return this.chatListCache$.pipe(
      map((cache) => {
        const cached = cache.get(orgId);
        if (!cached || this.isChatListStale(cached)) {
          return [];
        }
        return cached.list;
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    );
  }

  // ============================================
  // API Layer Methods
  // ============================================

  /**
   * Create new chat thread and initiate polling
   * Uses CreateChatRequest from ldt-chat-api
   * Polling is automatically managed by threadById$() subscriptions
   * @returns Observable of thread response with real-time message updates via polling
   */
  public createThread(
    orgId: string,
    prompt: string,
    mode?: CreateChatRequest.ModeEnum
  ): Observable<ChatThreadResponse> {
    const req: CreateChatRequest = { prompt, mode };

    this.setThreadLoading(null, true); // Use null as placeholder for new thread

    return this.chatApi
      .createChat(orgId, this.langGraphInstance.selectedInstanceValue ?? undefined, req)
      .pipe(
        tap((thread: any) => {
          this.updateThreadCache(thread as ChatThreadResponse);
          // Start polling with refCount=0 (actual subscriptions will increment)
          if ((thread as ChatThreadResponse).status === 'busy') {
            this.startPolling(thread.id, 0);
          }
          // Invalidate chat list cache so it refreshes with the new thread
          this.invalidateChatListCache(orgId);
        }),
        switchMap((thread) =>
          this.threadById$(thread.id).pipe(
            filter((t) => t !== null),
            map((t) => t as ChatThreadResponse)
          )
        ),
        catchError((err) => this.handleChatError(err, 'create'))
      );
  }

  /**
   * Continue existing thread with new message
   * Uses ContinueChatRequest from ldt-chat-api
   * Polling is automatically managed by threadById$() subscriptions
   * @returns Observable of updated thread with real-time updates via polling
   */
  public continueThread(
    orgId: string,
    threadId: string,
    prompt: string
  ): Observable<ChatThreadResponse> {
    const req: ContinueChatRequest = { prompt };

    this.setThreadLoading(threadId, true);

    return this.chatApi
      .continueChat(orgId, threadId, this.langGraphInstance.selectedInstanceValue ?? undefined, req)
      .pipe(
        tap((thread: any) => {
          this.updateThreadCache(thread as ChatThreadResponse);
          // Start polling with refCount=0 (actual subscriptions will increment)
          if ((thread as ChatThreadResponse).status === 'busy') {
            this.startPolling(threadId, 0);
          }
        }),
        switchMap(() =>
          this.threadById$(threadId).pipe(
            filter((t) => t !== null),
            map((t) => t as ChatThreadResponse)
          )
        ),
        catchError((err) => this.handleChatError(err, 'continue', threadId))
      );
  }

  /**
   * Get thread by ID with caching and request deduplication
   * Returns ChatThreadResponse from API
   * Automatically starts polling if thread status is 'busy'
   * Polling is automatically managed by threadById$() subscriptions
   * Deduplicates simultaneous requests for the same thread
   * @param useCache - If false, bypasses cache and fetches fresh data
   */
  public getThread(
    orgId: string,
    threadId: string,
    useCache = true
  ): Observable<ChatThreadResponse> {
    Log.d('PeopleChatService.getThread() called', { threadId, useCache, orgId });

    // Check cache first
    if (useCache) {
      const cached = this.threadsCache$.value.get(threadId);
      if (cached && !this.isStale(cached)) {
        Log.d('PeopleChatService: Returning cached thread', threadId);
        // If cached thread is busy and not already polling, start polling with refCount=0
        if (cached.status === 'busy' && !this.activePolls.has(threadId)) {
          this.startPolling(threadId, 0);
        }
        return of(cached);
      }
    }

    // Check if there's already an in-flight request for this thread
    const inFlight = this.inFlightRequests.get(threadId);
    if (inFlight) {
      Log.d('PeopleChatService: Reusing in-flight request for thread', threadId);
      return inFlight;
    }

    // Create new request
    Log.d('PeopleChatService: Creating NEW API request for thread', threadId);
    this.setThreadLoading(threadId, true);

    const request$ = this.chatApi
      .getChat(orgId, threadId, this.langGraphInstance.selectedInstanceValue ?? undefined)
      .pipe(
        tap((thread: any) => {
          const chatThread = thread as ChatThreadResponse;
          Log.d('PeopleChatService: API response received for thread', threadId);
          this.updateThreadCache(chatThread);

          // If thread is busy, start polling with refCount=0 (subscriptions will increment)
          if (chatThread.status === 'busy') {
            this.startPolling(threadId, 0);
          }
        }),
        map((thread) => thread as ChatThreadResponse),
        catchError((err) => {
          // Admin fallback: thread URLs only carry a thread id, so an internal
          // admin may open a link (external source, or a "show all chats"
          // history item) for a thread in an org they aren't scoped to. When
          // the org-scoped fetch 404s, retry via the admin endpoint, which can
          // read any thread by id.
          if (err?.status === 404 && this.authService.isAdminValue) {
            Log.d('PeopleChatService: org-scoped 404, retrying via admin endpoint', threadId);
            return this.getThreadViaAdmin(threadId).pipe(
              catchError((adminErr) => {
                Log.e('PeopleChatService: admin fallback failed for thread', threadId, adminErr);
                // Surface the original 404 to the user, not the admin error.
                return this.handleChatError(err, 'get', threadId);
              })
            );
          }

          Log.e('PeopleChatService: API error for thread', threadId, err);
          return this.handleChatError(err, 'get', threadId);
        }),
        // Clear the in-flight entry only once the whole chain settles (success
        // or error), including the admin fallback. Deleting earlier would let a
        // concurrent getThread() for the same id miss this shared request and
        // start a duplicate (potentially a duplicate admin fallback).
        finalize(() => this.inFlightRequests.delete(threadId)),
        shareReplay(1) // Share the result with multiple subscribers
      );

    // Store in-flight request
    this.inFlightRequests.set(threadId, request$);

    return request$;
  }

  /**
   * Fetch a thread via the internal admin endpoint, which can read any thread
   * by id regardless of the caller's org scope. Used as a fallback when the
   * org-scoped getThread 404s for an internal admin. The admin endpoint returns
   * the thread under a `chat` key in the same user-facing shape as the
   * org-scoped GET chat call; we unwrap it and feed it through the same
   * cache/polling pipeline so callers (and threadById$ subscribers) are
   * unaffected by where the thread came from.
   */
  private getThreadViaAdmin(threadId: string): Observable<ChatThreadResponse> {
    return this.adminApi
      .adminGetThread(threadId, this.langGraphInstance.selectedInstanceValue ?? undefined)
      .pipe(
        map((response: AdminGetThread200Response) => {
          const thread = response?.chat as ChatThreadResponse | undefined;
          if (!thread || !thread.id) {
            // No usable thread payload — treat as not found.
            const notFound = new Error('Thread not found via admin endpoint') as Error & {
              status: number;
            };
            notFound.status = 404;
            throw notFound;
          }
          return thread;
        }),
        tap((thread: ChatThreadResponse) => {
          Log.d('PeopleChatService: admin endpoint resolved thread', threadId);
          this.updateThreadCache(thread);

          // If thread is busy, start polling with refCount=0 (subscriptions increment)
          if (thread.status === 'busy') {
            this.startPolling(threadId, 0);
          }
        })
      );
  }

  /**
   * List all threads for organization
   * Returns array of ListChatItem from ldt-chat-api
   * Uses cache unless refresh=true or cache is stale
   * @param orgId - The organization ID
   * @param refresh - If true, bypasses cache and fetches fresh data
   */
  public listThreads(orgId: string, refresh = false): Observable<ListChatItem[]> {
    // Check cache first unless refresh requested
    if (!refresh) {
      const cached = this.chatListCache$.value.get(orgId);
      if (cached && !this.isChatListStale(cached)) {
        return of(cached.list);
      }
    }

    // Fetch from API and update cache
    return this.chatApi
      .listChats(orgId, this.langGraphInstance.selectedInstanceValue ?? undefined)
      .pipe(
        tap((response: any) => {
          const list = response.chats || [];
          this.updateChatListCache(orgId, list);
        }),
        map((response: any) => response.chats || []),
        catchError((err) => this.handleChatError(err, 'list'))
      );
  }

  /**
   * Submit feedback for thread
   * Uses CreateFeedbackRequest from ldt-chat-api
   */
  public submitFeedback(
    orgId: string,
    threadId: string,
    isPositive: boolean,
    comment?: string
  ): Observable<void> {
    const req: CreateFeedbackRequest = {
      feedback: isPositive ? 1 : 0,
      comment: comment || '',
    };

    return this.chatApi.createFeedback(orgId, threadId, undefined, req).pipe(
      catchError((err) => {
        this.notify.error('Error submitting feedback');
        return throwError(() => err);
      }),
      map(() => undefined)
    );
  }

  /**
   * Get company lookups in component-expected format (name -> id)
   * This is a convenience method that handles the reversal automatically
   */
  public getReversedCompanyLookupsForThread(threadId: string): Observable<Record<string, string>> {
    return this.threadById$(threadId).pipe(
      map((thread) => {
        if (!thread?.company_lookups) return {};

        // Reverse: API format (id -> name) to component format (name -> id)
        return Object.entries(thread.company_lookups).reduce(
          (acc, [id, name]) => {
            acc[name] = id;
            return acc;
          },
          {} as Record<string, string>
        );
      })
    );
  }

  /**
   * Seed a synthetic read-only thread into the cache for shared-link playback.
   * The returned threadId can be passed as a query param to /chat so the page
   * picks it up from the cache without ever hitting the API.
   */
  public seedSharedThread(config: {
    messages: Message[];
    company_lookups?: Record<string, string>;
    created_at?: string;
  }): string {
    const threadId = `shared-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const thread: ChatThreadResponse = {
      id: threadId,
      status: 'idle',
      messages: config.messages || [],
      company_lookups: config.company_lookups,
      created_at: config.created_at,
    };
    this.updateThreadCache(thread);
    return threadId;
  }

  /**
   * Synchronous snapshot of a cached thread (null if not in cache).
   */
  public getThreadSnapshot(threadId: string): ChatThreadResponse | null {
    return this.threadsCache$.value.get(threadId) || null;
  }

  /**
   * Cancel a running query for a chat thread
   * Stops polling and updates cache to reflect cancellation
   */
  public cancelRun(
    orgId: string,
    threadId: string
  ): Observable<CancelRun200Response> {
    return this.chatApi
      .cancelRun(orgId, threadId, this.langGraphInstance.selectedInstanceValue ?? undefined)
      .pipe(
        switchMap((response) => {
          if (response.status === 'cancelled') {
            this.stopPolling(threadId);
            // Refresh thread to get final state after cancellation
            // Swallow getChat errors — cancellation already succeeded
            return this.chatApi
              .getChat(orgId, threadId, this.langGraphInstance.selectedInstanceValue ?? undefined)
              .pipe(
                tap((thread: any) => {
                  this.updateThreadCache(thread as ChatThreadResponse);
                }),
                map(() => response),
                catchError(() => of(response))
              );
          }
          return of(response);
        }),
        catchError((err) => this.handleChatError(err, 'cancel', threadId))
      );
  }

  /**
   * Branch a conversation from a specific message into a new thread
   * Returns the new branched thread, starts polling if it's busy
   */
  public branchChat(
    orgId: string,
    chatId: string,
    messageId: string,
    prompt?: string
  ): Observable<ChatThreadResponse> {
    const req: BranchChatRequest = { message_id: messageId, prompt };

    return this.chatApi
      .branchChat(orgId, chatId, this.langGraphInstance.selectedInstanceValue ?? undefined, req)
      .pipe(
        tap((thread: any) => {
          const chatThread = thread as ChatThreadResponse;
          this.updateThreadCache(chatThread);
          if (chatThread.status === 'busy') {
            this.startPolling(chatThread.id, 0);
          }
          this.invalidateChatListCache(orgId);
        }),
        map((thread) => thread as ChatThreadResponse),
        catchError((err) => this.handleChatError(err, 'branch', chatId))
      );
  }

  /**
   * Edit a message and re-run the conversation from that point
   * Rewinds the same thread to the target message and runs with corrected prompt
   */
  public editAndRerun(
    orgId: string,
    chatId: string,
    messageId: string,
    prompt: string
  ): Observable<ChatThreadResponse> {
    const req: EditAndRerunRequest = { message_id: messageId, prompt };

    this.setThreadLoading(chatId, true);

    return this.chatApi
      .editAndRerun(orgId, chatId, this.langGraphInstance.selectedInstanceValue ?? undefined, req)
      .pipe(
        tap((thread: any) => {
          const chatThread = thread as ChatThreadResponse;
          this.updateThreadCache(chatThread);
          if (chatThread.status === 'busy') {
            this.startPolling(chatId, 0);
          }
        }),
        switchMap(() =>
          this.threadById$(chatId).pipe(
            filter((t) => t !== null),
            map((t) => t as ChatThreadResponse)
          )
        ),
        catchError((err) => this.handleChatError(err, 'edit', chatId))
      );
  }

  /**
   * Submit per-message feedback (thumbs up/down) for a specific assistant message
   */
  public submitMessageFeedback(
    orgId: string,
    threadId: string,
    messageId: string,
    isPositive: boolean,
    comment?: string
  ): Observable<void> {
    const req: CreateMessageFeedbackRequest = {
      feedback: isPositive ? 1 : 0,
      comment: comment || '',
    };

    return this.chatApi
      .createMessageFeedback(
        orgId,
        threadId,
        messageId,
        this.langGraphInstance.selectedInstanceValue ?? undefined,
        req
      )
      .pipe(
        catchError((err) => {
          this.notify.error('Error submitting feedback');
          return throwError(() => err);
        }),
        map(() => undefined)
      );
  }

  /**
   * Invalidate chat list cache for organization
   * Call this when a new thread is created to ensure the list updates
   * @param orgId - The organization ID
   */
  public invalidateChatListCache(orgId: string): void {
    const cache = this.chatListCache$.value;
    cache.delete(orgId);
    this.chatListCache$.next(new Map(cache));
  }

  // ============================================
  // Polling Implementation
  // ============================================

  /**
   * Increment polling reference count for a thread
   * Starts polling if this is the first subscriber
   */
  private incrementPollingRefCount(threadId: string): void {
    const pollingState = this.activePolls.get(threadId);
    if (pollingState) {
      pollingState.refCount++;
    }
  }

  /**
   * Decrement polling reference count for a thread
   * Stops polling if this was the last subscriber
   */
  private decrementPollingRefCount(threadId: string): void {
    const pollingState = this.activePolls.get(threadId);
    if (pollingState) {
      pollingState.refCount--;
      // Only stop polling if no more subscribers and not in busy state
      if (pollingState.refCount <= 0) {
        const cachedThread = this.threadsCache$.value.get(threadId);
        // Keep polling if thread is busy even with no subscribers (in case user navigates back)
        if (cachedThread?.status !== 'busy') {
          this.stopPolling(threadId);
        }
      }
    }
  }

  /**
   * Start polling for thread updates
   * Automatically stops when thread reaches terminal status or timeout
   * Implements exponential backoff for 5xx errors (max 10s)
   * Uses reference counting to share polling across multiple subscribers
   */
  private startPolling(threadId: string, initialRefCount: number = 1): void {
    Log.d('PeopleChatService.startPolling() called', { threadId, initialRefCount });

    // If already polling, just increment ref count
    const existingPoll = this.activePolls.get(threadId);
    if (existingPoll && existingPoll.isActive) {
      existingPoll.refCount += initialRefCount;
      Log.d('PeopleChatService: Reusing existing poll, refCount now:', existingPoll.refCount);
      return;
    }

    // Cancel any existing poll for this thread (shouldn't happen with new logic)
    this.stopPolling(threadId);
    Log.d('PeopleChatService: Starting NEW polling loop for thread', threadId);

    const config = this.config;
    const pollingState: PollingState = {
      threadId,
      timeoutId: 0,
      startTime: Date.now(),
      maxDuration: config.maxPollingDuration,
      interval: config.pollingInterval,
      isActive: true,
      retryCount: 0,
      currentBackoff: 0,
      refCount: initialRefCount,
    };

    // CRITICAL: Register polling state immediately to prevent race conditions
    // This ensures subsequent startPolling() calls will see the existing poll
    this.activePolls.set(threadId, pollingState);

    const poll = () => {
      // Check timeout
      if (Date.now() - pollingState.startTime > pollingState.maxDuration) {
        this.handlePollingTimeout(threadId);
        return;
      }

      const orgId = this.authService.getSelectedOrgIdValue;

      Log.d('PeopleChatService: Polling API call for thread', {
        threadId,
        refCount: pollingState.refCount,
      });

      this.chatApi
        .getChat(orgId, threadId, this.langGraphInstance.selectedInstanceValue ?? undefined)
        .pipe(
          tap((thread: any) => {
            const chatThread = thread as ChatThreadResponse;
            this.updateThreadCache(chatThread);

            // Reset retry state on successful response
            pollingState.retryCount = 0;
            pollingState.currentBackoff = 0;

            // Terminal statuses
            if (['idle', 'error', 'interrupted'].includes(chatThread.status)) {
              this.stopPolling(threadId);
              this.handleThreadComplete(chatThread);
              return;
            }

            // Schedule next poll
            if (pollingState.isActive) {
              pollingState.timeoutId = window.setTimeout(poll, pollingState.interval);
              this.activePolls.set(threadId, pollingState);
            }
          }),
          catchError((err) => {
            // Check if this is a 5xx server error
            const is5xxError = err.status >= 500 && err.status < 600;

            if (is5xxError) {
              // Implement exponential backoff for 5xx errors
              pollingState.retryCount++;

              // Calculate exponential backoff: 2^retryCount * 1000ms, max 10s
              const exponentialDelay = Math.pow(2, pollingState.retryCount) * 1000;
              pollingState.currentBackoff = Math.min(exponentialDelay, 10000);

              Log.s('Chat polling 5xx error - retrying with backoff', 'info', {
                thread: threadId,
                org: orgId,
                retryCount: pollingState.retryCount.toString(),
                errorStatus: err.status.toString(),
              });

              // Continue polling with backoff delay
              if (pollingState.isActive) {
                pollingState.timeoutId = window.setTimeout(poll, pollingState.currentBackoff);
                this.activePolls.set(threadId, pollingState);
              }
            } else {
              // Non-5xx errors stop polling
              this.notify.error('Error checking chat status');
              this.stopPolling(threadId);
            }

            return EMPTY;
          })
        )
        .subscribe();
    };

    // Initial delay before first poll
    setTimeout(() => poll(), config.initialPollDelay);
  }

  /**
   * Stop polling for specific thread
   */
  private stopPolling(threadId: string): void {
    const pollingState = this.activePolls.get(threadId);
    if (pollingState) {
      pollingState.isActive = false;
      if (pollingState.timeoutId) {
        clearTimeout(pollingState.timeoutId);
      }
      this.activePolls.delete(threadId);
    }
  }

  /**
   * Stop all active polling
   * Call this in service destroy or on org change
   */
  private stopAllPolling(): void {
    this.activePolls.forEach((_, threadId) => this.stopPolling(threadId));
  }

  private handlePollingTimeout(threadId: string): void {
    this.stopPolling(threadId);
    this.notify.error('Chat timed out. Please try again.');

    const error = new Error('Polling timeout exceeded');
    this.updateThreadError(threadId, error);

    Log.s('Chat polling timeout', 'error', {
      threadId,
      orgId: this.authService.getSelectedOrgIdValue,
    });
  }

  private handleThreadComplete(thread: ChatThreadResponse): void {
    // Emit completion event
    this.threadCompleteSubject$.next(thread);
  }

  // ============================================
  // Cache Management
  // ============================================

  /**
   * Update thread in cache and notify subscribers
   * Accepts ChatThreadResponse from API
   */
  private updateThreadCache(thread: ChatThreadResponse): void {
    const cache = this.threadsCache$.value;
    const cachedThread: CachedThread = {
      ...thread,
      _cachedAt: Date.now(),
    };
    cache.set(thread.id, cachedThread);
    this.threadsCache$.next(new Map(cache));

    // Update loading state
    this.setThreadLoading(thread.id, false);
  }

  /**
   * Check if cached thread is stale
   */
  private isStale(thread: CachedThread): boolean {
    if (!thread._cachedAt) return true;
    return Date.now() - thread._cachedAt > this.config.cacheThreadsMaxAge;
  }

  /**
   * Clear all cached threads
   */
  private clearThreadsCache(): void {
    this.threadsCache$.next(new Map());
  }

  /**
   * Remove specific thread from cache
   */
  private removeThreadFromCache(threadId: string): void {
    const cache = this.threadsCache$.value;
    cache.delete(threadId);
    this.threadsCache$.next(new Map(cache));
  }

  /**
   * Update chat list cache for organization
   */
  private updateChatListCache(orgId: string, list: ListChatItem[]): void {
    const cache = this.chatListCache$.value;
    cache.set(orgId, {
      list,
      cachedAt: Date.now(),
    });
    this.chatListCache$.next(new Map(cache));
  }

  /**
   * Check if cached chat list is stale
   */
  private isChatListStale(cached: { list: ListChatItem[]; cachedAt: number }): boolean {
    if (!cached.cachedAt) return true;
    return Date.now() - cached.cachedAt > this.config.cacheThreadsMaxAge;
  }

  /**
   * Clear chat list cache
   */
  private clearChatListCache(): void {
    this.chatListCache$.next(new Map());
  }

  /**
   * Set loading state for thread
   */
  private setThreadLoading(threadId: string | null, isLoading: boolean): void {
    const loading = this.loadingThreads$.value;
    if (threadId) {
      if (isLoading) {
        loading.add(threadId);
      } else {
        loading.delete(threadId);
      }
      this.loadingThreads$.next(new Set(loading));
    }
  }

  /**
   * Clear cache on org change
   */
  private initializeOrgChangeHandler(): void {
    this.authService.$getSelectedOrgId
      .pipe(
        distinctUntilChanged(),
        filter((orgId) => !!orgId)
      )
      .subscribe(() => {
        this.stopAllPolling();
        this.clearThreadsCache();
        this.clearChatListCache();
        this.loadingThreads$.next(new Set());
        this.errors$.next(new Map());
      });
  }

  // ============================================
  // Error Handling
  // ============================================

  private handleChatError(error: any, operation: string, threadId?: string): Observable<never> {
    // Quota exceeded
    if (error.status === 402) {
      this.notify.error(
        "You have reached your temporary chat limit for the month. We'll be in touch shortly."
      );
      Log.s('Chat quota exceeded', 'error', {
        operation,
        threadId: threadId || 'unknown',
        orgId: this.authService.getSelectedOrgIdValue,
      });
      return throwError(() => new Error('QUOTA_EXCEEDED'));
    }

    // Not found - clear from cache
    if (error.status === 404 && threadId) {
      this.notify.error('Chat thread not found');
      this.removeThreadFromCache(threadId);
      return throwError(() => new Error('THREAD_NOT_FOUND'));
    }

    // Generic error
    const message = `Error ${operation}ing chat. Please try again later.`;
    this.notify.error(message);

    Log.s(`Chat ${operation} error`, 'error', {
      error,
      threadId: threadId || '',
      orgId: this.authService.getSelectedOrgIdValue,
    });

    if (threadId) {
      this.updateThreadError(threadId, error);
      this.setThreadLoading(threadId, false);
    }

    return throwError(() => error);
  }

  private updateThreadError(threadId: string, error: Error): void {
    const errors = this.errors$.value;
    errors.set(threadId, error);
    this.errors$.next(new Map(errors));
  }
}
